/*
 * Rebuy Smart Cart — AJAX enroll / add-to-cart bridge
 * ---------------------------------------------------
 * Goal: when a visitor clicks an "Enroll" / add-to-cart CTA we want to
 *   1. NOT do a full-page redirect to /cart or ?viewcart=true,
 *   2. add the course via the Shopify AJAX Cart API,
 *   3. block adding the SAME course/variant twice (keep qty at 1),
 *   4. open the Rebuy Smart Cart drawer in place, and
 *   5. keep the theme cart-count badge in sync.
 *
 * Two cooperating layers:
 *
 *   A. Click take-over (capture phase, runs before Replo/theme handlers).
 *      For CTAs whose variant we can resolve with confidence, we fully own the
 *      click: prevent the default/redirect, dedupe, AJAX-add, open Rebuy.
 *
 *   B. Network safety-net (patches fetch + XHR, mirrors cart-quantity-limit.js).
 *      For any /cart/add that we DIDN'T take over (Replo's own multi-product
 *      buttons, the theme's product forms, permalinks, etc.) we still: clamp
 *      course variants back to qty 1 (dedupe), refresh the count, and open Rebuy.
 *
 * CTAs we deliberately DO NOT touch:
 *   - FAQ text auto-linkified as http://No.No
 *   - Discounted express-checkout permalinks:
 *       /cart/<variant>:1?discount=...&checkout[redirect_url]=/checkout
 *   - mailto: links
 *
 * "Course" products = every product Replo injects into #replo-deps-products.
 * Those are the ones capped at 1 per cart.
 */
(function () {
  'use strict';

  // Run once. If the asset is included twice we would register two capture-phase
  // click listeners and run handleEnroll twice per click (two identical
  // "[rebuy] selected variant" lines showed up in the Safari console).
  if (window.__rbSmartCartInit) return;
  window.__rbSmartCartInit = true;

  // Build marker — bump this on every deploy so we can prove, from the browser,
  // exactly which build of this file the live theme is actually serving.
  var RB_BUILD = 'RB-BUILD-2026-07-23-G';
  window.RB_BUILD = RB_BUILD;

  // --- TEMPORARY DIAGNOSTIC (remove once the Safari issue is closed) ----------
  // Rebuy's Smart Cart is a Vue app; on the Replo workshop page in Safari it
  // half-initialises (window.Rebuy exists with only `customer`, no SmartCart),
  // which is the signature of a script error during its boot. This file runs
  // before the app scripts, so it can capture those errors for inspection via
  // window.__rbErrors — reading them needs no devtools filtering.
  window.__rbErrors = window.__rbErrors || [];
  window.addEventListener('error', function (e) {
    try {
      window.__rbErrors.push({
        kind: e.message ? 'error' : 'resource',
        message: e.message || ('failed to load: ' + ((e.target && (e.target.src || e.target.href)) || '?')),
        source: e.filename || ((e.target && (e.target.src || e.target.href)) || ''),
        line: e.lineno || 0,
        stack: (e.error && e.error.stack) ? String(e.error.stack).slice(0, 400) : ''
      });
    } catch (err) {}
  }, true); // capture: also catches failed <script>/<img> loads, which don't bubble
  window.addEventListener('unhandledrejection', function (e) {
    try {
      var r = e.reason;
      window.__rbErrors.push({
        kind: 'promise',
        message: (r && (r.message || r.toString && r.toString())) || 'unhandled rejection',
        stack: (r && r.stack) ? String(r.stack).slice(0, 400) : ''
      });
    } catch (err) {}
  });
  // --- end temporary diagnostic ----------------------------------------------

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var INTERNAL_HEADER = 'X-Rebuy-Ajax';           // marks our own requests so the net-layer ignores them
  var DUPLICATE_MSG = 'This course is already in your cart.';
  var RB_DEBUG = true; // set to false once the variant matching is confirmed in the browser console

  // ---------------------------------------------------------------------------
  // Small AJAX helpers. All of OUR requests carry INTERNAL_HEADER so the
  // fetch/XHR interceptors below skip them (no recursion / double handling).
  // ---------------------------------------------------------------------------
  function ajax(url, init) {
    init = init || {};
    init.headers = Object.assign({ Accept: 'application/json' }, init.headers || {});
    init.headers[INTERNAL_HEADER] = '1';
    init.credentials = 'same-origin';
    return fetch(url, init);
  }
  function getCart() {
    // no-store: mobile browsers can hand back a cached /cart.js, which made us
    // think a course was in/out of the cart when the real cart said otherwise.
    return ajax(root + 'cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
  }
  function addVariant(id, qty) {
    return ajax(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty || 1 })
    }).then(function (r) { return r.json(); });
  }
  function updateLine(key, qty) {
    var updates = {}; updates[key] = qty;
    return ajax(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  // ---------------------------------------------------------------------------
  // Product / variant resolution from Replo's injected product JSON.
  // ---------------------------------------------------------------------------
  function collectProducts() {
    var out = [];
    // Multiple chunks can each emit an (id-duplicated) script — grab them all.
    var scripts = document.querySelectorAll('script[id="replo-deps-products"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var arr = JSON.parse(scripts[i].textContent);
        (arr || []).forEach(function (p) {
          if (p && p.data && Array.isArray(p.data.variants)) out.push(p.data);
        });
      } catch (e) { /* ignore malformed */ }
    }
    return out;
  }

  // Set of every variant id that belongs to a course product (capped at 1).
  function courseVariantSet() {
    var set = {};
    collectProducts().forEach(function (p) {
      (p.variants || []).forEach(function (v) { set[v.id] = true; });
    });
    return set;
  }

  function firstAvailableVariantId(product) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i].id;
    }
    return variants.length ? variants[0].id : null;
  }

  // Read the selected variant id STRICTLY from the product form the trigger
  // belongs to. No page-wide fallback: a global [name="id"] query would grab a
  // Rebuy cross-sell widget's form and add the WRONG product (that was the bug).
  function variantFromForm(trigger) {
    var form = trigger.closest && trigger.closest('form');
    if (!form) return null;
    var controls = Array.prototype.slice.call(form.querySelectorAll('[name="id"]'));
    if (form.id) {
      // Controls linked to THIS form via form="<id>" (e.g. the variant <select>).
      controls = controls.concat(Array.prototype.slice.call(
        document.querySelectorAll('[name="id"][form="' + form.id + '"]')));
    }
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.disabled || !el.value) continue;   // Shopify disables the unused hidden input
      var id = parseInt(el.value, 10);
      if (id) return id;
    }
    return null;
  }

  // Returns a numeric variant id, or null when we can't confidently pick one
  // (e.g. a page selling multiple different courses at once).
  function resolveVariant(trigger) {
    // 1. Explicit attribute, if a theme/dev ever adds one.
    var withAttr = trigger.closest && trigger.closest('[data-variant-id]');
    if (withAttr) {
      var idAttr = parseInt(withAttr.getAttribute('data-variant-id'), 10);
      if (idAttr) return idAttr;
    }
    // 2. The product form's selected variant (product pages / sticky bar).
    var fromForm = variantFromForm(trigger);
    if (fromForm) return fromForm;
    // 3. Single-product page -> its first available variant (the common case).
    var products = collectProducts();
    if (products.length === 1) return firstAvailableVariantId(products[0]);
    return null; // ambiguous -> defer to Replo + the network safety-net
  }

  // ---------------------------------------------------------------------------
  // Rebuy Smart Cart control. The exact API differs across Rebuy versions, so we
  // try the documented methods first and fall back to toggling the DOM state
  // that the drawer itself uses. If Rebuy has not booted yet we retry.
  // ---------------------------------------------------------------------------
  function tryRebuyOpen() {
    try {
      var R = window.Rebuy;
      if (R && R.SmartCart) {
        if (typeof R.SmartCart.refresh === 'function') { try { R.SmartCart.refresh(); } catch (e) {} }
        if (typeof R.SmartCart.show === 'function') { R.SmartCart.show(); return true; }
        if (typeof R.SmartCart.open === 'function') { R.SmartCart.open(); return true; }
        if (typeof R.SmartCart.toggle === 'function') { R.SmartCart.toggle(); return true; }
      }
      if (R && R.Cart && typeof R.Cart.open === 'function') { R.Cart.open(); return true; }
    } catch (e) { /* fall through to DOM fallback */ }
    return false;
  }

  // Mirror the open state we observed on the live drawer:
  //   <div id="rebuy-cart" class="rebuy-cart is-visible ..." aria-hidden="false">
  function domOpenFallback() {
    var el = document.getElementById('rebuy-cart');
    if (!el) return false;
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-visible');
    document.documentElement.classList.add('rebuy-cart-visible');
    if (document.body) document.body.classList.add('rebuy-cart-visible');
    return true;
  }

  // Undo exactly what domOpenFallback() applied. Without this, the html/body
  // `rebuy-cart-visible` class we added survives Rebuy's own close (Rebuy only
  // clears state it set itself) and the drawer could no longer be dismissed —
  // the CSS that actually reveals it is `.rebuy-cart-visible .rebuy-cart__background`.
  function domCloseFallback() {
    var el = document.getElementById('rebuy-cart');
    if (el) {
      el.classList.remove('is-visible');
      el.setAttribute('aria-hidden', 'true');
    }
    document.documentElement.classList.remove('rebuy-cart-visible');
    if (document.body) document.body.classList.remove('rebuy-cart-visible');
  }

  // Is the drawer actually on screen? Trust the DOM, not the return value of
  // show() — on mobile Rebuy.SmartCart.show() reports success but never opens
  // anything, so the drawer stays shut.
  //
  // aria-hidden must NOT be tested: Rebuy leaves aria-hidden="false" on
  // #rebuy-cart while the drawer is CLOSED, and treating that as "open" made this
  // return true immediately so the fallback never ran (the original iOS bug).
  // We check both states that can reveal it: Rebuy's own is-visible, and the
  // html-level class the CSS keys the backdrop off.
  function drawerIsOpen() {
    var el = document.getElementById('rebuy-cart');
    if (el && el.classList.contains('is-visible')) return true;
    return document.documentElement.classList.contains('rebuy-cart-visible');
  }

  function openRebuy() {
    var tries = 0, max = 65; // ~13s @ 200ms — long enough for a cold Rebuy boot
    (function attempt() {
      tryRebuyOpen();               // ask Rebuy's own API to open (desktop path)
      if (drawerIsOpen()) return;   // it really opened — done
      // Rebuy's API said "ok" but the drawer isn't visible (the mobile no-op).
      // Give show() one cycle to animate open, then force the open state Rebuy
      // itself uses so the drawer appears on mobile too.
      if (tries >= 1) { domOpenFallback(); if (drawerIsOpen()) return; }
      if (++tries >= max) return;   // #rebuy-cart not booted yet — give up quietly
      setTimeout(attempt, 200);
    })();
  }

  // Hold the drawer open after a click ("open intent").
  //
  // Rebuy can finish booting AFTER our click and reset the flyout to closed, so a
  // fast first click looked like nothing happened while a slow one worked — the
  // giveaway was that pasting a diagnostic into the console first (10s+ of delay)
  // made it work every time. A short window missed that late reset, so we hold the
  // intent for several seconds and keep re-asserting.
  //
  // The shopper is never trapped: clicking Rebuy's own close button or backdrop
  // drops the intent immediately (see the cancel listener below).
  var openIntentUntil = 0;

  // 12s, not 6s: on a cold/incognito first visit nothing is cached, so Rebuy can
  // take several seconds to boot and create #rebuy-cart. The first click used to
  // fall outside the old window (the element did not exist yet, so there was
  // nothing to open), while a second click worked because Rebuy was ready by then.
  // The loop below no-ops once the drawer is open and a shopper close cancels it,
  // so a longer window costs nothing.
  function holdDrawerOpen(ms) {
    openIntentUntil = Date.now() + (ms || 12000);
    (function again() {
      if (Date.now() > openIntentUntil) return;
      if (!drawerIsOpen()) {
        tryRebuyOpen();                              // keep Rebuy's own state in sync
        if (!drawerIsOpen()) domOpenFallback();      // and force it if that no-ops
      }
      setTimeout(again, 200);
    })();
  }

  // A real close by the shopper wins immediately and permanently: drop the intent
  // AND strip the state we added, otherwise our html/body class keeps the drawer
  // on screen after Rebuy has closed its own and it can never be dismissed.
  function cancelOpenIntent() {
    openIntentUntil = 0;
    domCloseFallback();                    // clear ours now...
    setTimeout(domCloseFallback, 60);      // ...and again after Rebuy's own close runs
    setTimeout(domCloseFallback, 250);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('#rebuy-cart-close, .rebuy-cart__flyout-close, .rebuy-cart__background, [data-rebuy-cart-close]')) {
      cancelOpenIntent();
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerIsOpen()) cancelOpenIntent();
  }, true);

  // Ask Rebuy to re-sync its contents after a cart mutation. Rebuy normally
  // auto-refreshes when it sees a /cart/add go by, but we nudge it to be safe.
  function refreshRebuy() {
    try {
      if (window.Rebuy && Rebuy.SmartCart && typeof Rebuy.SmartCart.refresh === 'function') {
        Rebuy.SmartCart.refresh();
      }
      document.dispatchEvent(new CustomEvent('rebuy:cart.change'));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {}
  }

  // Repopulate the drawer AFTER it is open. On mobile the flyout can mount showing
  // an empty/stale cart (the item is really in the cart — /cart proves it — but
  // Rebuy renders empty), and a single refresh() fired before the drawer mounts is
  // lost. So we nudge Rebuy a few times once it's on screen, trying whatever
  // re-render method this Rebuy build exposes. No cart mutation, no qty change.
  function resyncRebuyDrawer() {
    var tries = 0;
    (function again() {
      try {
        var R = window.Rebuy && window.Rebuy.SmartCart;
        if (R) {
          if (typeof R.refresh === 'function') R.refresh();
          else if (typeof R.update === 'function') R.update();
          else if (typeof R.reload === 'function') R.reload();
          else if (typeof R.render === 'function') R.render();
        }
        document.dispatchEvent(new CustomEvent('rebuy:cart.change'));
      } catch (e) {}
      if (++tries < 5) setTimeout(again, 300); // ~1.2s of nudges as the drawer settles
    })();
  }

  // ---------------------------------------------------------------------------
  // Enroll-button disable state — PER VARIANT.
  // We must NOT disable every enroll button just because the cart is non-empty
  // (that made "Idea to Empire" look enrolled after adding "No-Cost Boss").
  // A button is only disabled when ITS OWN resolved variant is in the cart.
  // ---------------------------------------------------------------------------
  var ENROLL_SELECTORS = '.Sd_addProduct, .add_to_cart, [data-replo-add-product-variant-to-cart]';
  // Every Replo add-to-cart button keeps the "enrolled" dimming but stays
  // clickable, so a repeat click re-opens the drawer instead of being swallowed
  // by a disabled state. handleEnroll still refuses to re-add a variant that is
  // already in the cart (no qty bump). The theme's own enroll button carries this
  // same attribute in product-form.liquid so it behaves identically.
  var ALWAYS_OPEN_SELECTOR = '[data-replo-add-product-variant-to-cart]';
  (function injectDisableStyle() {
    var s = document.createElement('style');
    s.id = 'rb-enroll-disable-style';
    s.textContent = '.rb-enrolled{pointer-events:none!important;opacity:.55!important;cursor:not-allowed!important;}' +
      // :not(.is-loading) keeps the button inert while an add is in flight, so a
      // fast double-click can't race two /cart/add calls past the dedupe check.
      '.rb-enrolled' + ALWAYS_OPEN_SELECTOR + ':not(.is-loading)' +
      '{pointer-events:auto!important;cursor:pointer!important;}';
    (document.head || document.documentElement).appendChild(s);
  })();

  function alwaysOpen(el) {
    return !!(el && el.matches && el.matches(ALWAYS_OPEN_SELECTOR));
  }

  function disableBtn(el, on) {
    if (!el) return;
    // A disabled <button> dispatches no click event at all, whatever the CSS says,
    // so opted-in buttons must never get the property or the ARIA state.
    var keepClickable = alwaysOpen(el);
    el.classList.toggle('rb-enrolled', on);
    if ('disabled' in el && !keepClickable) el.disabled = on;
    el.setAttribute('aria-disabled', (on && !keepClickable) ? 'true' : 'false');
  }

  function cartVariantIds(cart) {
    var ids = [];
    (cart && cart.items || []).forEach(function (it) { ids.push(Number(it.variant_id)); });
    return ids;
  }

  // Disable only the buttons whose variant is already in the cart; enable the rest.
  function syncEnrollButtons(cart) {
    var inCart = cartVariantIds(cart);
    if (RB_DEBUG) console.debug('[rebuy] cart variant ids:', inCart);
    var btns = document.querySelectorAll(ENROLL_SELECTORS);
    for (var i = 0; i < btns.length; i++) {
      var v = resolveVariant(btns[i]);
      disableBtn(btns[i], v != null && inCart.indexOf(Number(v)) > -1);
    }
  }

  // ---------------------------------------------------------------------------
  // Theme cart-count badge ( [data-cart-count], see header-icons.liquid ).
  // ---------------------------------------------------------------------------
  function updateCount(cart) {
    if (!cart) return;
    var n = cart.item_count || 0;
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = n < 100 ? String(n) : '';
      badges[i].style.display = n > 0 ? '' : 'none';
    }
    syncEnrollButtons(cart); // per-variant enable/disable
  }

  // ---------------------------------------------------------------------------
  // Tiny toast for the "already in cart" message.
  // ---------------------------------------------------------------------------
  function toast(msg) {
    var t = document.getElementById('rb-course-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'rb-course-toast';
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;z-index:2147483647;left:50%;bottom:32px;' +
        'transform:translateX(-50%);background:#111;color:#fff;padding:12px 20px;' +
        'border-radius:8px;font-family:Rubik,sans-serif;font-size:15px;line-height:1.3;' +
        'box-shadow:0 6px 24px rgba(0,0,0,.28);opacity:0;transition:opacity .25s ease;' +
        'max-width:90vw;text-align:center;pointer-events:none;';
      (document.body || document.documentElement).appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(function () { t.style.opacity = '1'; });
    clearTimeout(t._hide);
    t._hide = setTimeout(function () { t.style.opacity = '0'; }, 3200);
  }

  // ---------------------------------------------------------------------------
  // Layer A — we fully own this enroll click.
  // ---------------------------------------------------------------------------
  // Show/hide a lightweight loading state on the clicked button so the click
  // feels instant while we add + prime Rebuy in the background.
  function setBusy(el, on) {
    if (!el) return;
    try {
      el.classList.toggle('is-loading', on);
      var sp = el.querySelector('.svg-loader');
      if (sp) sp.style.display = on ? 'inline-block' : 'none';
      el.style.pointerEvents = on ? 'none' : '';
    } catch (e) {}
  }

  function handleEnroll(variantId, trigger) {
    var wanted = Number(variantId);
    setBusy(trigger, true);
    getCart().then(function (cart) {
      var inCart = cartVariantIds(cart);
      if (RB_DEBUG) console.debug('[rebuy] selected variant:', wanted, '| cart variants:', inCart);
      // Match ONLY the exact selected variant id — never product title/handle/type.
      var already = inCart.indexOf(wanted) > -1;
      if (already) {
        // Rule 2: never bump to qty 2 — item is already there, just show it.
        disableBtn(trigger, true);
        toast(DUPLICATE_MSG);
        // Open FIRST so the flyout is mounted, THEN repopulate it. Refreshing
        // before open was lost on mobile and the drawer showed "cart is empty".
        openRebuy();
        holdDrawerOpen();            // survive a late Rebuy boot/reset
        resyncRebuyDrawer();
        return;
      }
      // Rule 3: a different variant is always allowed. Add FIRST so the item is
      // in the cart, prime Rebuy, THEN open — the drawer shows the product on
      // open with no empty flash.
      return addVariant(wanted, 1).then(function () {
        disableBtn(trigger, true);   // disable only THIS button (this variant)
        openRebuy();
        holdDrawerOpen();            // Rebuy's post-add re-render / late boot must not shut it
        resyncRebuyDrawer();
        getCart().then(updateCount); // badge + per-variant sync, non-blocking
      });
    }).catch(function () {
      refreshRebuy();
      openRebuy();
    }).then(function () { setBusy(trigger, false); });
  }

  // Decide whether a click is an enroll CTA we should own.
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    if (!e.target.closest) return;

    var themeBtn = e.target.closest('.Sd_addProduct, .add_to_cart');
    var reploBtn = e.target.closest('[data-replo-add-product-variant-to-cart]');
    var link = e.target.closest('a[href]');

    // The theme's native "Enroll Now" / add-to-cart submit button. We must beat
    // the theme's own delegated handler (cart-ajax.js) so its mini-cart drawer
    // never opens and we show Rebuy instead.
    if (themeBtn) {
      var themeVariant = resolveVariant(themeBtn);
      if (!themeVariant) return; // can't resolve -> let the theme handle it
      e.preventDefault();
      e.stopImmediatePropagation();
      handleEnroll(themeVariant, themeBtn);
      return;
    }

    // A Replo add-to-cart button -> add the page's course.
    if (reploBtn) {
      var variantId = resolveVariant(reploBtn);
      if (!variantId) return; // ambiguous -> let Replo add it; safety-net handles Rebuy + dedupe
      e.preventDefault();
      e.stopImmediatePropagation();
      handleEnroll(variantId, reploBtn);
      return;
    }

    // A plain "view cart" / "I'm ready" link (href pathname exactly /cart).
    if (link) {
      var u;
      try { u = new URL(link.href, location.origin); } catch (err) { return; }
      if (u.hostname !== location.hostname) return;                       // external
      if (/checkout|redirect|discount/.test(u.search)) return;           // express-checkout permalink -> leave
      if (u.pathname.replace(/\/+$/, '') !== '/cart') return;            // not a bare /cart link
      e.preventDefault();
      e.stopImmediatePropagation();
      var v = resolveVariant(link);
      if (v) handleEnroll(v, link); else openRebuy();
    }
  }, true); // capture phase: beat Replo's own click handler

  // ---------------------------------------------------------------------------
  // Layer B — network safety-net for adds we did not take over.
  // Clamp course variants to qty 1 (dedupe), refresh count, open Rebuy.
  // ---------------------------------------------------------------------------
  function isCartAdd(url) {
    return !!url && /\/cart\/add(\.js)?(\?|$)/.test(String(url));
  }
  function isCartChange(url) {
    return !!url && /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(String(url));
  }

  // Re-sync count + enroll-disable state after a non-add cart mutation, e.g. the
  // shopper removes the course inside the Rebuy drawer -> button re-enables.
  function afterCartChange() {
    getCart().then(updateCount).catch(function () {});
  }

  var handling = false; // guard against re-entrancy
  function afterExternalAdd() {
    if (handling) return;
    handling = true;
    openRebuy(); // surface the drawer immediately; content syncs below
    getCart().then(function (cart) {
      var courses = courseVariantSet();
      var updates = {}, clamped = false;
      (cart.items || []).forEach(function (it) {
        if (courses[it.variant_id] && it.quantity > 1) { updates[it.key] = 1; clamped = true; }
      });
      if (clamped) {
        return updateLine(Object.keys(updates)[0], 1).then(function () {
          // set any remaining over-cap lines too
          var keys = Object.keys(updates);
          if (keys.length <= 1) return getCart();
          return Promise.all(keys.slice(1).map(function (k) { return updateLine(k, 1); }))
            .then(function () { return getCart(); });
        }).then(function (c2) {
          toast(DUPLICATE_MSG);
          updateCount(c2);
          refreshRebuy();
          openRebuy();
        });
      }
      updateCount(cart);
      refreshRebuy();
      openRebuy();
    }).catch(function () {
      openRebuy();
    }).then(function () { handling = false; });
  }

  function headerIsInternal(headers) {
    if (!headers) return false;
    if (typeof headers.get === 'function') return !!headers.get(INTERNAL_HEADER);
    return !!(headers[INTERNAL_HEADER] || headers[INTERNAL_HEADER.toLowerCase()]);
  }

  // --- Patch fetch (Replo + modern add-to-cart) ---
  //
  // This wrapper sits in front of EVERY fetch on the page — including the Rebuy
  // app's own loader, because theme.liquid loads this file (line 5) before
  // content_for_header (line 23). So it must be bulletproof:
  //
  //   1. Call the original with `window` as the receiver. This file is 'use
  //      strict', so a bare fetch(url) call arrives here with `this` undefined;
  //      Safari strictly enforces the Window receiver on fetch and threw a
  //      TypeError, which took Rebuy's bundle down with it. Chrome is lenient,
  //      which is exactly why the drawer only ever broke in Safari.
  //   2. Never throw. If our own bookkeeping fails we still return the real
  //      request, so a bug in here can never break another app's networking.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url, internal;
      try {
        url = (typeof input === 'string') ? input : (input && input.url);
        internal = (init && headerIsInternal(init.headers)) ||
                   (input && input.headers && headerIsInternal(input.headers));
      } catch (e) {
        return nativeFetch.apply(window, arguments);
      }
      var promise = nativeFetch.apply(window, arguments);
      try {
        if (!internal && isCartAdd(url)) {
          return promise.then(function (resp) {
            if (resp && resp.ok) setTimeout(afterExternalAdd, 0);
            return resp;
          });
        }
        if (!internal && isCartChange(url)) {
          return promise.then(function (resp) {
            if (resp && resp.ok) setTimeout(afterCartChange, 0);
            return resp;
          });
        }
      } catch (e) { /* fall through to the untouched promise */ }
      return promise;
    };
  }

  // --- Patch XHR (the theme's jQuery add-to-cart) ---
  var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHR && XHR.open && XHR.send) {
    var origOpen = XHR.open;
    var origSend = XHR.send;
    XHR.open = function (method, url) {
      this.__rbIsAdd = isCartAdd(url);
      this.__rbIsChange = !this.__rbIsAdd && isCartChange(url);
      return origOpen.apply(this, arguments);
    };
    XHR.send = function () {
      if (this.__rbIsAdd || this.__rbIsChange) {
        var isAdd = this.__rbIsAdd;
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(isAdd ? afterExternalAdd : afterCartChange, 0);
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // On load: sync the per-variant enroll-disable state + badge to the real cart
  // (covers back/forward + bfcache restore). Only buttons for variants actually
  // in the cart get disabled.
  function initSync() {
    getCart().then(updateCount).catch(function () {});
    if (/[?&]viewcart=true/.test(location.search)) openRebuy();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSync);
  } else {
    initSync();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) initSync(); });
})();
